import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  JwtRefresher,
  AutocompleteContainer,
  AutocompleteHeader,
} from '@white-nl/instantsearch-components';
import { history } from 'instantsearch.js/es/lib/routers';
/* eslint-disable-next-line import/no-extraneous-dependencies */
import qs from 'qs';
/* eslint-disable-next-line import/no-extraneous-dependencies */
import SearchkitClient from '@searchkit/instantsearch-client';
import {
  Configure,
  InstantSearch,
} from 'react-instantsearch';
import { useGetSearchTranslatesQuery } from '../../redux-modules/service';

export default function SiteSearchBox({
  indexName,
  publicUrl,
  refreshUrl,
  siteId,
  userGroups,
  placeholder,
  searchUrl,
}) {
  const [
    currentSearchClient,
    setCurrentSearchClient,
  ] = useState(null);

  const {
    data: translatesData,
    isFetching: isTranslatesDataFetching,
  } = useGetSearchTranslatesQuery();

  if (isTranslatesDataFetching || translatesData.translates.length === 0) {
    return (
      <>
        <label className="visually-hidden" htmlFor="header-search">Search</label>
        <input className="form-control" id="header-search" type="search" placeholder={placeholder} disabled />
      </>
    );
  }

  const {
    search,
  } = translatesData.translates;

  let defaultFilters = `siteId:${siteId}`;

  let qUserGroups = userGroups.split(',');
  qUserGroups = qUserGroups.join(' OR userGroups:');
  defaultFilters = `${defaultFilters} AND (${qUserGroups && `userGroups:${qUserGroups} OR `}userGroups:all)`;

  const queryParam = {
    queryStringToFilters: (queryString, mapping = {}) => {
      const result = {};

      const q = qs.parse(queryString);

      const filters = (q);

      Object.keys(filters).forEach((attribute) => {
        const key = mapping[attribute] || attribute;
        const values = filters[attribute] || [];
        result[key] = (Array.isArray(values) ? values : [values])
          .filter(Boolean)
          .map(queryParam.decodeURIComponent);
      });

      return result;
    },
    decodeURIComponent(encodedURIComponent) {
      try {
        return decodeURIComponent(encodedURIComponent);
      } catch (_) {
        /**
         * By default, percentage character (%) is not encoded.
         * Following function should find single percentage character (%),
         * not followed by two hexadecimal digits and encode it.
         */
        return decodeURIComponent(encodedURIComponent.replace(/%(?![0-9a-fA-F]{2})/g, '%25'));
      }
    },
  };

  const jwtRefresher = (
    <JwtRefresher
      cookieName="elasticsearch_token"
      checkIntervalSeconds={5}
      refreshTimeBeforeExpirySeconds={30}
      refreshIframeUrl={refreshUrl}
      onRefreshed={(token) => {
        const config = {
          url: publicUrl,
          headers: {
            authorization: `Bearer ${token}`,
          },
        };
        const searchKitClient = Object.assign(SearchkitClient(config), {
          transporter: {
            headers: {
              'x-algolia-application-id': 'NULL',
              'x-algolia-api-key': 'NULL',
            },
          },
        });
        setCurrentSearchClient(searchKitClient);
      }}
    />
  );

  if (!currentSearchClient) {
    return jwtRefresher;
  }

  return (
    <>
      {jwtRefresher}
      <InstantSearch
        indexName={indexName}
        searchClient={currentSearchClient}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
        routing={{
          router: history({

            parseURL({ location }) {
              const searchQ = location.search;

              const values = queryParam.queryStringToFilters(searchQ.slice(1), {});
              const result = {};

              result.query = values.query;

              return result;
            },
            writeDelay: 0,
          }),
          stateMapping: {
            stateToRoute(uiState) {
              const indexUiState = uiState[indexName];
              const result = {};

              result.query = indexUiState?.query;

              return result;
            },
            routeToState(routeState) {
              const result = {
                query: routeState.query
                && Array.isArray(routeState.query)
                && routeState.query.length > 0 ? routeState.query[0] : undefined,
              };

              return {
                [indexName]: result,
              };
            },
          },
        }}
      >
        <Configure
          filters={defaultFilters}
        />
        <AutocompleteContainer
          classNames={{
            input: 'form-control',
            submitButton: 'submit',
            detachedSearchButtonQuery: 'form-control order-2',
            detachedSearchButtonPlaceholder: 'form-control order-1',
            detachedSearchButtonIcon: 'submit order-3',
            detachedOverlay: 'header-portal-form-search',
            inputWrapperSuffix: 'd-none',
            inputWrapperPrefix: 'order-2',
            inputWrapper: 'order-1',
            label: 'm-0',
          }}
          searchClient={currentSearchClient}
          defaultIndexName={indexName}
          defaultFilters={defaultFilters}
          sources={[
            {
              getQueryParams: (query, defFilters) => ({
                query,
                filters: `${defFilters} AND section:products`,
              }),
              templates: {
                /* eslint-disable-next-line react/no-unstable-nested-components */
                header({ items }) {
                  if (items.length === 0) {
                    return null;
                  }

                  return <AutocompleteHeader title={Craft.t(search, 'products')} />;
                },
              },
            },
            {
              getQueryParams: (query, defFilters) => ({
                query,
                filters: `${defFilters} AND section:productCategories`,
              }),
              templates: {
                /* eslint-disable-next-line react/no-unstable-nested-components */
                header({ items }) {
                  if (items.length === 0) {
                    return null;
                  }

                  return <AutocompleteHeader title={Craft.t(search, 'productCategories')} />;
                },
              },
            },
            {
              getQueryParams: (query, defFilters) => ({
                query,
                filters: `${defFilters} AND section:pages`,
              }),
              templates: {
                /* eslint-disable-next-line react/no-unstable-nested-components */
                header({ items }) {
                  if (items.length === 0) {
                    return null;
                  }

                  return <AutocompleteHeader title={Craft.t(search, 'pages')} />;
                },
              },
            },
            {
              getQueryParams: (query, defFilters) => ({
                query,
                filters: `${defFilters} AND section:news`,
              }),
              templates: {
                /* eslint-disable-next-line react/no-unstable-nested-components */
                header({ items }) {
                  if (items.length === 0) {
                    return null;
                  }

                  return <AutocompleteHeader title={Craft.t(search, 'news')} />;
                },
              },
            },
          ]}
          onSubmit={(params) => {
            window.location.href = `${searchUrl}?query=${params.state.query}`;
          }}
          placeholder={Craft.t(search, 'What are you looking for?')}
          additionalData={[]}
        />
      </InstantSearch>
    </>
  );
}

SiteSearchBox.propTypes = {
  indexName: PropTypes.string,
  publicUrl: PropTypes.string,
  refreshUrl: PropTypes.string,
  userGroups: PropTypes.string,
  placeholder: PropTypes.string,
  siteId: PropTypes.number,
  searchUrl: PropTypes.string,
};
